<template>
    <div class="ds-dossier-section-head" style="border-bottom: 0; padding-bottom: 0">
        <div class="ds-dossier-chips" role="group" aria-label="Период">
            <button
                v-for="option in EXCHANGE_PERIODS"
                :key="option.id"
                type="button"
                class="ds-dossier-chip"
                :aria-pressed="period === option.id"
                @click="period = option.id"
            >{{ option.label }}</button>
        </div>
    </div>
    <span class="ds-dossier-note">По всем подразделениям</span>

    <!-- GET /api/user/{id}/exchange-summary/: отклики считаются по дате отклика, отработанное — по дате смены. -->
    <DossierState :status="summaryState.status" :height="220" error-text="Не удалось загрузить данные биржи" @retry="retrySummary" />
    <template v-if="summary">
        <div v-if="isEmpty" class="ds-dossier-state is-plain">Через биржу пока не работал</div>
        <template v-else>
            <div class="ds-dossier-tiles">
                <div class="ds-dossier-tile is-exchange">
                    <span class="ds-dossier-tile-label">Отработано</span>
                    <span class="ds-dossier-tile-value is-compact">{{ summary.workedShifts === null ? DASH : pluralShifts(summary.workedShifts) }}</span>
                    <span class="ds-dossier-tile-note">{{ formatHours(summary.workedHours) }} ч</span>
                </div>
                <div class="ds-dossier-tile">
                    <span class="ds-dossier-tile-label">Откликов</span>
                    <span class="ds-dossier-tile-value is-compact">{{ show(summary.responsesCount) }}</span>
                    <span class="ds-dossier-tile-note">одобрено {{ show(summary.approvedCount) }} · откл. {{ show(summary.rejectedCount) }}</span>
                </div>
                <div class="ds-dossier-tile" :class="{ 'is-violation': (summary.noShowCount ?? 0) > 0 }">
                    <span class="ds-dossier-tile-label">Неявки</span>
                    <span class="ds-dossier-tile-value is-compact">{{ show(summary.noShowCount) }}</span>
                    <span class="ds-dossier-tile-note">из {{ show(summary.approvedCount) }} одобренных</span>
                </div>
            </div>

            <div class="ds-dossier-section-head">
                <span class="ds-dossier-section-title">По магазинам</span>
                <span v-if="summary.departmentsTotal" class="ds-dossier-muted">топ {{ summary.topDepartments.length }} из {{ summary.departmentsTotal }}</span>
            </div>
            <div v-if="!summary.topDepartments.length" class="ds-dossier-state is-plain">Отработанных смен за период нет</div>
            <!-- Контракт отдаёт только число смен: часы по магазину не выводятся. -->
            <div v-else class="ds-dossier-pairs">
                <template v-for="item in summary.topDepartments" :key="item.id">
                    <span>{{ item.name || DASH }}</span><span>{{ item.shifts === null ? DASH : pluralShifts(item.shifts) }}</span>
                </template>
            </div>

            <div class="ds-dossier-section-head"><span class="ds-dossier-section-title">Последние отклики</span></div>
            <div v-if="!summary.recentResponses.length" class="ds-dossier-state is-plain">Откликов за период нет</div>
            <DossierResponseList v-else :responses="summary.recentResponses" />
            <!-- N — count отдельного vacancy-responses с теми же границами. -->
            <button type="button" class="ds-dossier-link" @click="emit('open-responses')">Все отклики · {{ responsesCountText }} ›</button>
        </template>
    </template>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import type { Dayjs } from 'dayjs'
import { DASH, formatHours, pluralShifts } from './dossierModel'
import { type ExchangePeriod, exchangeRange } from './dossierActivity'
import type { DossierData } from './useDossierData'
import DossierResponseList from './DossierResponseList.vue'
import DossierState from './DossierState.vue'

const props = defineProps<{
    data: DossierData
    // Опорный месяц периода и конец периода (сегодня; у архива — конец месяца увольнения).
    anchor: Dayjs
    end: Dayjs
}>()
const period = defineModel<ExchangePeriod>('period', { required: true })
const emit = defineEmits<{ 'open-responses': [] }>()

const EXCHANGE_PERIODS: { id: ExchangePeriod, label: string }[] = [
    { id: '3m', label: '3 месяца' },
    { id: 'year', label: 'Год' },
    { id: 'all', label: 'Всё время' },
]

const range = computed(() => exchangeRange(period.value, props.anchor, props.end))
const responsesQuery = computed(() => ({ range: range.value, page: 1 }))
watchEffect(() => {
    props.data.exchangeSummary.ensure(range.value)
    props.data.responses.ensure(responsesQuery.value)
})

const summaryState = computed(() => props.data.exchangeSummary.read(range.value))
const summary = computed(() => (summaryState.value.status === 'ready' ? summaryState.value.data : null))
const retrySummary = () => props.data.exchangeSummary.retry(range.value)
// Пусто — ни откликов, ни отработанных смен за период.
const isEmpty = computed(() => summary.value?.responsesCount === 0 && summary.value?.workedShifts === 0)

const responsesState = computed(() => props.data.responses.read(responsesQuery.value))
const responsesCountText = computed(() => {
    const state = responsesState.value
    if (state.status === 'loading') return '…'
    return state.data?.count ?? DASH
})

const show = (value: number | null) => (value === null ? DASH : String(value))
</script>
